import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, LayoutTemplate } from "lucide-react";
import { api, LandingPreviewPage } from "@/lib/api";
import {
  JOURNEY_WINDOW_OPTIONS,
  JourneyWindowId,
  journeyWindowParams,
} from "@/lib/journeyWindow";
import {
  buildSectionAudits,
  iframeUrl,
  type VerdictId,
} from "@/lib/landingSectionAudit";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { InfoTip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return "-";
  return `${Math.round(v * 1000) / 10}%`;
}

const VERDICT_STYLE: Record<
  VerdictId,
  { label: string; badge: string; card: string; cardActive: string }
> = {
  ok: {
    label: "OK",
    badge: "bg-emerald-700 text-white",
    card: "border-emerald-400 bg-emerald-100 hover:bg-emerald-200/90",
    cardActive: "border-emerald-600 bg-emerald-200 ring-2 ring-emerald-500/50",
  },
  uwaga: {
    label: "Uwaga",
    badge: "bg-amber-700 text-white",
    card: "border-amber-400 bg-amber-100 hover:bg-amber-200/90",
    cardActive: "border-amber-600 bg-amber-200 ring-2 ring-amber-500/50",
  },
  do_poprawy: {
    label: "Do poprawy",
    badge: "bg-rose-700 text-white",
    card: "border-rose-400 bg-rose-100 hover:bg-rose-200/90",
    cardActive: "border-rose-600 bg-rose-200 ring-2 ring-rose-500/50",
  },
  brak: {
    label: "Brak sygnału",
    badge: "bg-slate-600 text-white",
    card: "border-slate-300 bg-slate-100 hover:bg-slate-200/90",
    cardActive: "border-slate-500 bg-slate-200 ring-2 ring-slate-400/50",
  },
};

export default function Landingi() {
  const [period, setPeriod] = useState<JourneyWindowId>("7");
  const [pageId, setPageId] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState(0);

  const catalogQuery = useQuery({
    queryKey: ["landing-preview-catalog"],
    queryFn: api.landingPreviewCatalog,
    retry: false,
  });

  const pages = catalogQuery.data?.pages ?? [];
  const activePage: LandingPreviewPage | undefined =
    pages.find((p) => p.page_id === pageId) || pages[0];

  const effectivePageId = activePage?.page_id ?? "";

  const journeyQuery = useQuery({
    queryKey: ["landing-preview-journey", effectivePageId, period],
    queryFn: () =>
      api.siteAnalyticsJourney({
        ...journeyWindowParams(period),
        page_id: effectivePageId,
      }),
    enabled: Boolean(effectivePageId),
    retry: false,
  });

  const pageColumn = useMemo(() => {
    const cols = journeyQuery.data?.sections_by_page?.pages ?? [];
    return (
      cols.find((c) => c.page_id === effectivePageId) || cols[0] || null
    );
  }, [journeyQuery.data, effectivePageId]);

  const audits = useMemo(
    () =>
      buildSectionAudits(
        pageColumn?.sections ?? [],
        catalogQuery.data?.section_labels,
      ),
    [pageColumn, catalogQuery.data?.section_labels],
  );

  const issueCount = audits.filter(
    (a) => a.verdict === "do_poprawy" || a.verdict === "uwaga",
  ).length;

  const previewSrc = activePage
    ? iframeUrl(activePage.live_url, selectedSection)
    : "";

  const onSelectPage = (id: string) => {
    setPageId(id);
    setSelectedSection(null);
    setIframeKey((k) => k + 1);
  };

  const onSelectSection = (sectionId: string) => {
    setSelectedSection(sectionId);
    setIframeKey((k) => k + 1);
  };

  return (
    <div className="flex min-h-[calc(100vh-6rem)] flex-col">
      <PageHeader
        title="Landingi"
        subtitle="Podgląd live + metryki sekcji z journey. Kliknij wiersz, żeby przewinąć podgląd do tej części strony."
        actions={
          activePage ? (
            <Button variant="outline" size="sm" asChild>
              <a
                href={activePage.live_url}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Otwórz live
              </a>
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {pages.map((p) => (
          <Button
            key={p.page_id}
            size="sm"
            variant={p.page_id === effectivePageId ? "default" : "outline"}
            onClick={() => onSelectPage(p.page_id)}
          >
            {p.label}
          </Button>
        ))}
        {catalogQuery.isLoading && (
          <span className="text-xs text-muted-foreground">Katalog…</span>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {JOURNEY_WINDOW_OPTIONS.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            variant={opt.id === period ? "default" : "outline"}
            onClick={() => setPeriod(opt.id)}
          >
            {opt.label}
          </Button>
        ))}
        <span className="ml-1 text-xs text-muted-foreground">
          {journeyQuery.data?.period?.label_pl ?? "czas PL"}
          {pageColumn?.sessions != null
            ? ` · ${pageColumn.sessions} wizyt na stronie`
            : ""}
          {issueCount > 0 ? ` · ${issueCount} do przeglądu` : ""}
        </span>
      </div>

      <p className="mb-3 text-sm text-muted-foreground">
        Kolumna po prawej to te same sekcje co w Sprzedaż → Sekcje, tylko przy
        podglądzie strony. Procent = ile wizyt dojechało do sekcji.
        <InfoTip tipId="section_reach" className="ml-1 inline-flex align-middle" />
        <InfoTip tipId="section_cta" className="ml-1 inline-flex align-middle" />
      </p>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(280px,0.9fr)]">
        <Card className="flex min-h-[420px] flex-col overflow-hidden lg:min-h-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 border-b py-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <LayoutTemplate className="h-4 w-4 text-muted-foreground" />
              Podgląd
            </CardTitle>
            {activePage && (
              <span className="max-w-[60%] truncate font-mono text-[11px] text-muted-foreground">
                {activePage.live_url}
                {selectedSection ? `#${selectedSection}` : ""}
              </span>
            )}
          </CardHeader>
          <CardContent className="relative flex-1 p-0">
            {!activePage && (
              <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                Brak katalogu landingów.
              </div>
            )}
            {activePage && (
              <iframe
                key={`${iframeKey}-${previewSrc}`}
                title={`Podgląd ${activePage.label}`}
                src={previewSrc}
                className="absolute inset-0 h-full w-full border-0 bg-background"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                referrerPolicy="no-referrer-when-downgrade"
              />
            )}
          </CardContent>
        </Card>

        <Card className="flex max-h-[70vh] flex-col overflow-hidden lg:max-h-none">
          <CardHeader className="border-b py-3">
            <CardTitle className="text-base">Sekcje i metryki</CardTitle>
            <p className="text-xs text-muted-foreground">
              Werdykt: spadek ≥25 pp = do poprawy, 12–25 pp = uwaga. Klik =
              skok do #id w podglądzie.
            </p>
          </CardHeader>
          <CardContent className="flex-1 space-y-2 overflow-y-auto p-3">
            {journeyQuery.isLoading && (
              <p className="py-6 text-sm text-muted-foreground">
                Wczytywanie metryk…
              </p>
            )}
            {journeyQuery.isError && (
              <p className="py-6 text-sm text-muted-foreground">
                Nie udało się wczytać journey.
              </p>
            )}
            {journeyQuery.data && audits.length === 0 && (
              <p className="py-6 text-sm text-muted-foreground">
                Brak danych o sekcjach dla tej strony w wybranym okresie.
              </p>
            )}
            {audits.map((row) => {
              const v = VERDICT_STYLE[row.verdict];
              const s = row.section;
              const active = selectedSection === s.section_id;
              const clicks = s.cta_clicks ?? 0;
              const topCta = (s.ctas ?? [])[0];
              return (
                <button
                  key={s.section_id}
                  type="button"
                  onClick={() => onSelectSection(s.section_id)}
                  className={cn(
                    "w-full rounded-lg border px-3 py-2.5 text-left transition-colors",
                    active ? v.cardActive : v.card,
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        <span className="mr-1 tabular-nums text-muted-foreground">
                          {s.order + 1}.
                        </span>
                        {row.label}
                      </div>
                      <div className="truncate font-mono text-[10px] text-muted-foreground">
                        #{s.section_id}
                      </div>
                    </div>
                    <span
                      className={cn(
                        "shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
                        v.badge,
                      )}
                    >
                      {v.label}
                    </span>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                    <span title={`${s.sessions_reached} wizyt`}>
                      dojście {pct(s.view_rate)}
                    </span>
                    {row.dropPp != null && row.dropPp > 0.5 && (
                      <span>drop −{Math.round(row.dropPp)} pp</span>
                    )}
                    <span
                      className={
                        clicks > 0 ? "text-foreground/80" : undefined
                      }
                    >
                      CTA{" "}
                      {clicks > 0
                        ? `${clicks} · ${pct(s.cta_rate_of_viewers)} widzów`
                        : "–"}
                    </span>
                  </div>
                  {topCta ? (
                    <div className="mt-1 truncate text-[10px] text-muted-foreground">
                      {topCta.label} · {topCta.clicks}
                    </div>
                  ) : null}
                  <p className="mt-1.5 text-[11px] leading-snug text-muted-foreground">
                    {row.reason}
                  </p>
                </button>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
